import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Database, Clock, Users, FolderOpen, BarChart3, Wifi, WifiOff, Download } from "lucide-react";
import { useCachedMainPageData, useCachedReports, useCacheInvalidation, useMigrationHelper } from '@/hooks/useCachedData';

export const CacheDemo: React.FC = () => {
  const { 
    users, 
    reportGroups, 
    usersLoading, 
    reportGroupsLoading, 
    refetchAll 
  } = useCachedMainPageData();
  
  const { 
    invalidateAll, 
    invalidateUsers, 
    invalidateReportGroups 
  } = useCacheInvalidation();
  
  const { logCacheStatus, forceRefreshAll } = useMigrationHelper();
  
  const reportsQuery = useCachedReports();

  // PWA Cache Management Functions
  const clearServiceWorkerCache = async () => {
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
        console.log('[Cache] Service worker cache cleared');
      }
      
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[Cache] Browser caches cleared:', cacheNames);
      }
      
      // Force reload after clearing
      window.location.reload();
    } catch (error) {
      console.error('[Cache] Error clearing cache:', error);
    }
  };

  const checkServiceWorkerStatus = async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      const controller = navigator.serviceWorker.controller;
      
      console.log('[SW] Registration:', registration);
      console.log('[SW] Controller:', controller);
      console.log('[SW] State:', controller?.state);
      
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        console.log('[Cache] Available caches:', cacheNames);
        
        for (const cacheName of cacheNames) {
          const cache = await caches.open(cacheName);
          const keys = await cache.keys();
          console.log(`[Cache] ${cacheName} contains:`, keys.map(req => req.url));
        }
      }
    }
  };

  const forceServiceWorkerUpdate = async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      console.log('[SW] Update check triggered');
      
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      }
    }
  };

  const testOfflineMode = () => {
    // Simulate offline by trying to fetch a non-existent resource
    fetch('/test-offline-fallback')
      .then(() => console.log('[Test] Online'))
      .catch(() => console.log('[Test] Offline fallback working'));
  };
  
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Database className="h-6 w-6" />
          Cache Demo & PWA Status
        </h2>
        <div className="flex gap-2">
          <Button 
            onClick={logCacheStatus}
            variant="outline"
            size="sm"
          >
            <Database className="h-4 w-4 mr-2" />
            Log Cache
          </Button>
          <Button 
            onClick={forceRefreshAll}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Force Refresh
          </Button>
        </div>
      </div>

      {/* PWA Cache Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            PWA Cache Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              onClick={clearServiceWorkerCache}
              variant="destructive"
              size="sm"
            >
              <Database className="h-4 w-4 mr-2" />
              Clear All Cache
            </Button>
            <Button 
              onClick={checkServiceWorkerStatus}
              variant="outline"
              size="sm"
            >
              <Wifi className="h-4 w-4 mr-2" />
              Check SW Status
            </Button>
            <Button 
              onClick={forceServiceWorkerUpdate}
              variant="outline"
              size="sm"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Force SW Update
            </Button>
            <Button 
              onClick={testOfflineMode}
              variant="outline"
              size="sm"
            >
              <WifiOff className="h-4 w-4 mr-2" />
              Test Offline
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Sử dụng các nút này để kiểm tra và quản lý cache của PWA. 
            Xem Console (F12) để theo dõi logs chi tiết.
          </p>
        </CardContent>
      </Card>

      {/* Cache Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        {/* Users Cache */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users Cache
              {usersLoading && <Skeleton className="h-4 w-4 rounded-full" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Count:</span>
                <Badge variant="secondary">{users.length}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Badge variant={usersLoading ? "default" : "outline"}>
                  {usersLoading ? "Loading" : "Cached"}
                </Badge>
              </div>
              <Button 
                onClick={invalidateUsers}
                size="sm"
                variant="ghost"
                className="w-full"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Invalidate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report Groups Cache */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Groups Cache
              {reportGroupsLoading && <Skeleton className="h-4 w-4 rounded-full" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Count:</span>
                <Badge variant="secondary">{reportGroups.length}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Badge variant={reportGroupsLoading ? "default" : "outline"}>
                  {reportGroupsLoading ? "Loading" : "Cached"}
                </Badge>
              </div>
              <Button 
                onClick={invalidateReportGroups}
                size="sm"
                variant="ghost"
                className="w-full"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Invalidate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reports Cache */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Reports Cache
              {reportsQuery.isLoading && <Skeleton className="h-4 w-4 rounded-full" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Count:</span>
                                 <Badge variant="secondary">
                   {Array.isArray(reportsQuery.data?.data) 
                     ? reportsQuery.data.data.length 
                     : Array.isArray(reportsQuery.data) 
                     ? reportsQuery.data.length 
                     : 0}
                 </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Badge variant={reportsQuery.isLoading ? "default" : "outline"}>
                  {reportsQuery.isLoading ? "Loading" : "Cached"}
                </Badge>
              </div>
              <Button 
                onClick={() => reportsQuery.refetch()}
                size="sm"
                variant="ghost"
                className="w-full"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Refetch
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cache Benefits Demo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Cache Benefits & PWA Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Ứng dụng sử dụng caching thông minh và PWA để tăng hiệu năng. Thử các tính năng sau:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium">✅ Instant Loading:</h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Navigate giữa các trang - không loading</li>
                  <li>• Data hiển thị ngay từ cache</li>
                  <li>• Background updates tự động</li>
                  <li>• Offline support cho static content</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium">⚡ Performance Gains:</h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Giảm API calls đến server</li>
                  <li>• Tiết kiệm bandwidth</li>
                  <li>• Auto-update khi có version mới</li>
                  <li>• Smart cache invalidation</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={refetchAll} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refetch All Data
              </Button>
              <Button onClick={invalidateAll} variant="outline" size="sm">
                <Database className="h-4 w-4 mr-2" />
                Clear Query Cache
              </Button>
              <Button onClick={logCacheStatus} variant="outline" size="sm">
                <BarChart3 className="h-4 w-4 mr-2" />
                View Cache Stats
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 